<script setup>
import {useArticleApi} from '~/composables/api/useArticleApi'
import PostItem from '~/components/partials/PostItem.vue'
import BreadCrumb from '~/components/common/BreadCrumb.vue'
import FilterSelector from '~/components/common/FilterSelector.vue'
import {blogDescriptions, blogTitles} from '~/utils/blogUtils'
import {useArticleCategoryApi} from '~/composables/api/useArticleCategoryApi'
import Paginator from 'primevue/paginator'

const route = useRoute()
const blogSlug = route.params.blogSlug
const loading = ref(false)
const config = useRuntimeConfig()
const selectedCategories = ref([])
const currentBlogTitle = computed(() => blogTitles[blogSlug])
const currentBlogDescription = computed(() => blogDescriptions[blogSlug])
const searchParams = ref({
  sort: '-createdAt',
  limit: 8,
  page: 1,
  filters: {
    collection: ''
  }
})

const {data, error, refresh} = await useAsyncData(`blog-${blogSlug}`, async () => {
  try {
    loading.value = true
    const articlesApi = useArticleApi()
    const articleCategoryApi = useArticleCategoryApi()

    const sortParam = searchParams.value.sort
    const filterParam = searchParams.value.filters
    const pageParam = searchParams.value.page
    const limitParam = searchParams.value.limit
    const [articles, categories] = await Promise.all([articlesApi.getList(blogSlug, sortParam, filterParam, pageParam, limitParam), articleCategoryApi.getCategoriesByBlog(blogSlug, sortParam, filterParam)])
    loading.value = false
    return {
      articles: articles.items,
      pagination: articles.pagination,
      categories: categories.filter(item => item.articles_count > 0).map(item => ({label: item.title, value: item.id})),
      breadcrumbs: [
        {label: '', link: '/'},
        {label: currentBlogTitle.value, link: `/blog/${blogSlug}`}
      ]
    }
  } catch (e) {
    loading.value = false
    console.error(e)
    throw createError({statusCode: 404, message: 'Página no encontrada'})
  }
})

if (!data.value || error.value) {
  throw createError({statusCode: 404, statusMessage: 'page_not_found', fatal: true})
}

const totalItems = computed(() => {
  return data.value?.pagination?.count || 0
})
const startAt = computed(() => {
  return (searchParams.value.page - 1) * searchParams.value.limit
})

useCustomSEOData({
  title: currentBlogTitle.value,
  meta: [
    {
      hid: 'description',
      name: 'description',
      content: currentBlogDescription.value
    },
    {
      name: 'robots',
      content: 'index, follow',
    }
  ]
})

function categoryFieldChanged(value) {
  searchParams.value.filters['category'] = selectedCategories.value.join(',')
  refresh()
}

function onPageChange(event) {
  console.info(event)
  searchParams.value.page = (event.page + 1)
  refresh()
}
</script>

<template>
  <div class="grid grid-cols-12 bg-[#fcfbfa]">
    <div class="col-span-12 mb-5">
      <h1 class="text-2xl lg:text-4xl font-bold italic px-4 lg:px-8 underline text-[#363330] mt-5">{{
          currentBlogTitle
        }}</h1>
      <BreadCrumb class="px-4 lg:px-8" :items="data.breadcrumbs"/>
      <div class="text-xs px-4 lg:px-8 mb-4">
        <FilterSelector
          v-model="selectedCategories"
          :options="data.categories"
          option-label="label"
          option-value="value"
          title="Categoría"
          @update:model-value="categoryFieldChanged"
        />
      </div>
    </div>
  </div>

  <div class="grid grid-cols-12 px-4 lg:px-8 md:gap-4 lg:gap-10 mb-4">
    <Transition>
      <div v-show="loading" class="col-span-12">
        <i class="pi pi-spin pi-spinner" style="font-size: 1rem"/>
        <span class="ml-2">Cargando artículos</span>
      </div>
    </Transition>
    <PostItem v-for="article in data.articles" :key="article.id" :data="article"/>
  </div>
  <div class="px-0 sm:px-4 lg:px-8 md:gap-4 lg:gap-10 mb-12 flex justify-center">
    <Paginator :rows="searchParams.limit" :totalRecords="totalItems" :first="startAt" :pageLinkSize="3"
               @page="onPageChange"></Paginator>
  </div>
</template>
